import * as React from 'react';
import {
    FilterList,
    FilterListItem,
    FilterButton,
    FilterForm,
    SearchInput,
    DateInput,
    ReferenceInput,
    Button,
    useListContext,
    SelectArrayInput,
} from 'react-admin';
import { Box, Stack, SxProps, Theme } from '@mui/material';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import {
    startOfWeek,
    startOfMonth,
    subMonths,
    startOfToday,
    endOfToday,
    endOfWeek,
    endOfMonth,
    addMonths,
} from 'date-fns';
import { useConfigurationContext } from '@app/root/ConfigurationContext';
import { dateFilterInputProps } from '../../../core/utils/dateFilterInputProps';

export const TaskListFilter = ({
    disableSaveQuery,
    sx,
}: {
    disableSaveQuery?: boolean;
    sx?: SxProps<Theme>;
}) => {
    const { setFilters, filterValues } = useListContext();
    const { taskTypeLabels } = useConfigurationContext();

    const taskFilters = [
        <SearchInput
            source="search"
            alwaysOn
            placeholder="Search task text or type"
        />,
        <DateInput
            source="dueDate.gte"
            label="Due date after"
            {...dateFilterInputProps}
        />,
        <DateInput
            source="dueDate.lte"
            label="Due date before"
            {...dateFilterInputProps}
        />,
        <DateInput
            source="doneDate.gte"
            label="Done date after"
            {...dateFilterInputProps}
        />,
        <DateInput
            source="doneDate.lte"
            label="Done date before"
            {...dateFilterInputProps}
        />,
        <DateInput
            source="updatedAt.gte"
            label="Updated after"
            {...dateFilterInputProps}
        />,
        <DateInput
            source="updatedAt.lte"
            label="Updated before"
            {...dateFilterInputProps}
        />,
        <ReferenceInput
            source="assignedMemberId"
            reference="member"
            sort={{ field: 'lastName', order: 'ASC' }}
        />,
        <SelectArrayInput
            source="type.in"
            label="Type"
            choices={taskTypeLabels.map(type => ({
                id: type.value,
                name: type.label,
            }))}
        />,
    ];

    const hasFilters = filterValues && Object.keys(filterValues).length > 0;

    const handleClearFilters = () => {
        setFilters({});
    };

    return (
        <Box minWidth="10em" maxWidth="18em" order={-1} mr={2} sx={sx}>
            <Stack flexDirection={'row'} justifyContent={'space-between'}>
                <FilterButton
                    filters={taskFilters}
                    disableSaveQuery={disableSaveQuery}
                />
                <Button
                    disabled={!hasFilters}
                    label="Clear all"
                    onClick={handleClearFilters}
                    variant="outlined"
                    size="small"
                />
            </Stack>
            <FilterForm filters={taskFilters} />

            <FilterList label="Due date" icon={<AssignmentTurnedInIcon />}>
                <FilterListItem
                    label="Overdue"
                    value={{
                        doneDate: 'is.null',
                        dueDate: { lt: startOfToday().toISOString() },
                    }}
                />
                <FilterListItem
                    label="Today"
                    value={{
                        doneDate: 'is.null',
                        dueDate: {
                            gte: startOfToday().toISOString(),
                            lte: endOfToday().toISOString(),
                        },
                    }}
                />
                <FilterListItem
                    label="This week"
                    value={{
                        doneDate: 'is.null',
                        dueDate: {
                            gte: startOfWeek(new Date()).toISOString(),
                            lte: endOfWeek(new Date()).toISOString(),
                        },
                    }}
                />
                <FilterListItem
                    label="This month"
                    value={{
                        doneDate: 'is.null',
                        dueDate: {
                            gte: startOfMonth(new Date()).toISOString(),
                            lte: endOfMonth(new Date()).toISOString(),
                        },
                    }}
                />
                <FilterListItem
                    label="After this month"
                    value={{
                        doneDate: 'is.null',
                        dueDate: {
                            gte: startOfMonth(
                                addMonths(new Date(), 1)
                            ).toISOString(),
                        },
                    }}
                />
                <FilterListItem
                    label="Before this month"
                    value={{
                        doneDate: 'is.null',
                        dueDate: {
                            lte: subMonths(
                                startOfMonth(new Date()),
                                1
                            ).toISOString(),
                        },
                    }}
                />
            </FilterList>

            <FilterList label="Status" icon={<AssignmentTurnedInIcon />}>
                <FilterListItem
                    label="Pending"
                    value={{ doneDate: 'is.null' }}
                />
                <FilterListItem
                    label="Done"
                    value={{ doneDate: 'not.is.null' }}
                />
                <FilterListItem
                    label="Done today"
                    value={{
                        doneDate: {
                            gte: startOfToday().toISOString(),
                            lte: endOfToday().toISOString(),
                        },
                    }}
                />
                <FilterListItem
                    label="Done this week"
                    value={{
                        doneDate: {
                            gte: startOfWeek(new Date()).toISOString(),
                            lte: endOfWeek(new Date()).toISOString(),
                        },
                    }}
                />
                <FilterListItem
                    label="Done this month"
                    value={{
                        doneDate: {
                            gte: startOfMonth(new Date()).toISOString(),
                            lte: endOfMonth(new Date()).toISOString(),
                        },
                    }}
                />
            </FilterList>
        </Box>
    );
};
